import moment from "moment";
import { post } from "app/popleads/networking/RequestService";

/**
 * The assistant's conversation archive.
 *
 * Persisted server-side, per user — `listAssistantConversations` / `saveAssistantConversation` /
 * `deleteAssistantConversation` (see `WS.java`'s `askAssistant` neighbours and
 * `AssistantConversationDBOp`). Used to live only in the browser's `localStorage`, which meant no
 * history on a second device and nothing left after clearing site data; the backend now scopes
 * everything to the logged-in user, so this file is a thin wrapper over `post()` rather than the
 * store itself.
 *
 * The archive is capped server-side (30 most recently updated conversations) rather than here.
 */

const TITLE_LENGTH = 64;

/** The first thing the user actually asked. The greeting is the assistant talking to itself. */
export const conversationTitle = (messages = []) => {
    const firstQuestion = messages.find((message) => message.role === "user");
    if (!firstQuestion) return "New conversation";

    /* Answers arrive as HTML and a question can be echoed back through the same field, so strip
       tags before measuring — otherwise a title can be all markup and no words. */
    const text = String(firstQuestion.content ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    if (!text) return "New conversation";

    return text.length > TITLE_LENGTH ? `${text.slice(0, TITLE_LENGTH).trimEnd()}...` : text;
};

/** Newest first — the backend already orders and caps this. Fails soft to an empty list, the
 *  same way the old localStorage read did, so a load failure does not take the drawer down. */
export const loadConversations = async () => {
    try {
        const res = await post({ fn: "listAssistantConversations" });
        const conversations = res?.data?.conversations;
        return Array.isArray(conversations) ? conversations : [];
    } catch (e) {
        return [];
    }
};

/** Upsert by id — absent `id` creates a conversation and the response carries the server-assigned
 *  one, present `id` updates the caller's own row. Left to the caller to decide whether a thread
 *  is worth archiving at all and to handle a failed save; unlike `loadConversations` this does not
 *  fail soft, since silently dropping a save would look like the conversation was archived when
 *  it was not. */
export const saveConversation = async (conversation) => {
    const res = await post({
        fn: "saveAssistantConversation",
        id: conversation.id,
        title: conversationTitle(conversation.messages),
        messages: JSON.stringify(conversation.messages),
    });
    return res?.data?.conversation ?? null;
};

export const deleteConversation = (id) => post({ fn: "deleteAssistantConversation", id });

/**
 * Date buckets, in the order they should be rendered. Relative headings rather than dates, because
 * a list of past conversations is read as "how long ago", not as a calendar.
 */
export const groupConversations = (conversations) => {
    const today = moment().startOf("day");
    const buckets = [
        { label: "Today", items: [] },
        { label: "Yesterday", items: [] },
        { label: "Previous 7 days", items: [] },
        { label: "Older", items: [] },
    ];

    conversations.forEach((conversation) => {
        const days = today.diff(moment(conversation.updatedTime).startOf("day"), "days");

        if (days <= 0) buckets[0].items.push(conversation);
        else if (days === 1) buckets[1].items.push(conversation);
        else if (days <= 7) buckets[2].items.push(conversation);
        else buckets[3].items.push(conversation);
    });

    return buckets.filter((bucket) => bucket.items.length > 0);
};
